"use client";

import { Link } from "@/i18n/navigation";
import { useTranslations, useLocale } from "next-intl";
import { blogPosts, formatDate } from "@/lib/blog-posts";
import { getTranslatedBlogPost } from "@/lib/blog-post-translations";
import { AnnouncementBar } from "@/components/announcement-bar";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { WhatsAppButton } from "@/components/whatsapp-button";
import { MobileTabBar } from "@/components/mobile-tab-bar";

export function BlogIndex() {
  const t = useTranslations("blog");
  const tBlog = useTranslations("blogPage");
  const tCommon = useTranslations("common");
  const locale = useLocale();
  const sorted = [...blogPosts]
    .map((p) => {
      const tr = getTranslatedBlogPost(p.slug, locale);
      return tr ? { ...p, title: tr.title, excerpt: tr.excerpt, content: tr.content } : p;
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <div className="content-wrapper py-12 lg:py-20">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-text-tertiary mb-8">
            <Link href="/" className="hover:text-foreground transition-colors">
              {tCommon("home")}
            </Link>
            <span>/</span>
            <span className="text-text-secondary">{tBlog("breadcrumb")}</span>
          </nav>

          <div className="max-w-3xl">
            <p className="label-sm mb-3">{t("label")}</p>
            <h1 className="heading-lg mb-6">{t("title")}</h1>
            <p className="text-text-body text-[15px] leading-relaxed mb-12 max-w-xl">
              {tBlog("description")}
            </p>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-8 mb-12 pb-8 border-b border-border">
            <div>
              <span className="block font-[family-name:var(--font-cormorant)] text-3xl font-light text-foreground">
                {sorted.filter((p) => p.category === "Tutorial").length}
              </span>
              <span className="text-xs text-text-tertiary">
                {tBlog("videoTutorials")}
              </span>
            </div>
            <div className="w-px h-10 bg-border" />
            <div>
              <span className="block font-[family-name:var(--font-cormorant)] text-3xl font-light text-foreground">
                {sorted.filter((p) => p.category === "Informatie").length}
              </span>
              <span className="text-xs text-text-tertiary">{tBlog("articles")}</span>
            </div>
          </div>

          {/* Post grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {sorted.map((post) => (
              <div key={post.slug}>
              <Link
                href={`/blog/${post.slug}`}
                className="group block border border-border hover:bg-surface transition-colors rounded-2xl overflow-hidden"
                style={{
                  transitionDuration: "var(--duration-fast)",
                  transitionTimingFunction: "var(--ease-out)",
                }}
              >
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden bg-surface">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                    style={{
                      transitionDuration: "var(--duration-slow)",
                      transitionTimingFunction: "var(--ease-out)",
                    }}
                  />
                  {post.youtubeId && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 bg-foreground/80 flex items-center justify-center group-hover:bg-foreground transition-colors rounded-xl">
                        <svg
                          className="w-5 h-5 text-background ml-0.5"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5 lg:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="label-sm text-cta">
                      {tBlog(post.category === "Tutorial" ? "categoryTutorial" : "categoryInformatie")}
                    </span>
                    <span className="text-xs text-text-tertiary">
                      {formatDate(post.date, locale)}
                    </span>
                  </div>
                  <h2 className="text-lg font-[family-name:var(--font-cormorant)] font-medium text-foreground mb-2 group-hover:text-foreground/80 transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-sm text-text-secondary leading-relaxed line-clamp-2">
                    {post.excerpt}
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 mt-4 label-sm text-foreground"
                  >
                    {t("readMore")}
                    <svg
                      className="w-3.5 h-3.5 translate-x-0 group-hover:translate-x-1 transition-transform"
                      style={{ transitionDuration: "var(--duration-fast)" }}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    >
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  </span>
                </div>
              </Link>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileTabBar />
    </>
  );
}
